/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  CustomReportCategoryType,
  CustomReportCategory
} from '../../../../../../../redux/context/customReports/typesCategory'
import { generateRandomId, createBuilderNode } from '../utils'
import { BuilderNode, NodesById, NormalizedBuilderNode, State, VisitorContext } from '../types'

/**
 * Optimized function to add a child node
 * @param nodesById Current normalized tree state
 * @param parentId ID of the parent node
 * @param nodeType Type of node to create
 * @param context Visitor context
 * @returns Updated normalized tree state
 */
export const addChild = (
  nodesById: NodesById,
  parentId: string,
  nodeType: CustomReportCategoryType,
  context: VisitorContext
): NodesById => {
  const newNodeId = generateRandomId()
  const baseNode = createBuilderNode(newNodeId, nodeType, context)

  if (!nodesById[parentId]) {
    throw new Error(`Parent node "${parentId}" not found`)
  }

  const { children, ...rest } = baseNode
  const newNode: NormalizedBuilderNode = {
    ...rest,
    parentId,
    childIds: []
  }

  return {
    ...nodesById,
    [newNodeId]: newNode,
    [parentId]: {
      ...nodesById[parentId],
      childIds: [...(nodesById[parentId].childIds || []), newNodeId]
    }
  }
}

/**
 * Optimized function to remove a node and all its descendants
 * @param nodesById Current normalized tree state
 * @param nodeId ID of the node to remove
 * @returns Updated normalized tree state
 */
export const removeNode = (nodesById: NodesById, nodeId: string): NodesById => {
  // Get all descendant node IDs to remove
  const getAllDescendants = (id: string): string[] => {
    const node = nodesById[id]
    if (!node) return []

    return [id, ...(node.childIds || []).flatMap(childId => getAllDescendants(childId))]
  }

  const idsToRemove = getAllDescendants(nodeId)
  const parentId = nodesById[nodeId]?.parentId

  // Create new state with nodes removed
  const newState = { ...nodesById }

  // Remove all descendant nodes
  idsToRemove.forEach(id => {
    delete newState[id]
  })

  // Update parent's childIds list
  if (parentId && newState[parentId]) {
    newState[parentId] = {
      ...newState[parentId],
      childIds: newState[parentId].childIds.filter(id => id !== nodeId)
    }
  }

  return newState
}

/**
 * Optimized function to toggle node expansion
 * @param nodesById Current normalized tree state
 * @param nodeId ID of the node to toggle
 * @returns Updated normalized tree state
 */
export const toggleExpand = (nodesById: NodesById, nodeId: string): NodesById => {
  if (!nodesById[nodeId]) return nodesById

  return {
    ...nodesById,
    [nodeId]: {
      ...nodesById[nodeId],
      expanded: !nodesById[nodeId].expanded
    }
  }
}

/**
 * Optimized function to update node values
 * @param nodesById Current normalized tree state
 * @param nodeId ID of the node to update
 * @param values Partial values to update
 * @returns Updated normalized tree state
 */
export const updateNodeValues = (
  nodesById: NodesById,
  nodeId: string,
  values: Partial<CustomReportCategory>
): NodesById => {
  if (!nodesById[nodeId]) return nodesById

  return {
    ...nodesById,
    [nodeId]: {
      ...nodesById[nodeId],
      ...values
    }
  } as NodesById
}

/**
 * Optimized function to handle node dragging
 * @param nodesById Current normalized tree state
 * @param nodeId ID of the node being dragged
 * @param isDragging Whether the node is being dragged or not
 * @returns Updated normalized tree state
 */
export const setNodeDragging = (nodesById: NodesById, nodeId: string, isDragging: boolean): NodesById => {
  if (!nodesById[nodeId]) return nodesById

  return {
    ...nodesById,
    [nodeId]: {
      ...nodesById[nodeId],
      isDragging
    }
  }
}

/**
 * Optimized function to move a node to a new position
 * @param nodesById Current normalized tree state
 * @param draggedNodeId ID of the node being dragged
 * @param targetNodeId ID of the target node
 * @param position Position relative to target ('before', 'after', or 'inside')
 * @returns Updated normalized tree state
 */
export const moveNode = (
  nodesById: NodesById,
  draggedNodeId: string,
  targetNodeId: string,
  position: 'before' | 'after' | 'inside'
): NodesById => {
  const draggedNode = nodesById[draggedNodeId]
  const targetNode = nodesById[targetNodeId]

  if (!draggedNode || !targetNode) return nodesById

  // Don't allow moving a node into itself or its descendants
  if (draggedNodeId === targetNodeId) return nodesById

  // Check if target is a descendant of dragged node
  const isDescendant = (parent: string, child: string): boolean => {
    const node = nodesById[parent]
    if (!node) return false

    if (node.childIds.includes(child)) return true

    return node.childIds.some(id => isDescendant(id, child))
  }

  if (isDescendant(draggedNodeId, targetNodeId)) return nodesById

  // Create new state
  const newState = { ...nodesById }

  // Remove from old parent
  const oldParentId = draggedNode.parentId
  if (oldParentId) {
    newState[oldParentId] = {
      ...newState[oldParentId],
      childIds: newState[oldParentId].childIds.filter(id => id !== draggedNodeId)
    }
  }

  // Add to new location
  if (position === 'inside') {
    // Add as child of target
    newState[targetNodeId] = {
      ...newState[targetNodeId],
      childIds: newState[targetNodeId].childIds.includes(draggedNodeId)
        ? newState[targetNodeId].childIds
        : [...newState[targetNodeId].childIds, draggedNodeId],
      expanded: true // Expand target to show the new child
    }

    // Update dragged node's parent reference
    newState[draggedNodeId] = {
      ...newState[draggedNodeId],
      parentId: targetNodeId,
      isDragging: false
    }
  } else {
    // Add before or after target at same level
    const targetParentId = targetNode.parentId

    if (targetParentId) {
      const siblings = [...newState[targetParentId].childIds]
      const targetIndex = siblings.indexOf(targetNodeId)

      if (position === 'before') {
        siblings.splice(targetIndex, 0, draggedNodeId)
      } else {
        // after
        siblings.splice(targetIndex + 1, 0, draggedNodeId)
      }

      newState[targetParentId] = {
        ...newState[targetParentId],
        childIds: siblings
      }

      // Update dragged node's parent reference
      newState[draggedNodeId] = {
        ...newState[draggedNodeId],
        parentId: targetParentId,
        isDragging: false
      }
    }
  }

  return newState
}

/**
 * Convert normalized tree to hierarchical structure for visitors
 * @param nodesById Normalized tree state
 * @param rootIds Array of root node IDs (usually just one)
 * @returns Array of hierarchical BuilderNode trees
 */
export const buildTreeStructure = (nodesById: NodesById, rootIds: string[]): BuilderNode[] => {
  const buildTree = (nodeId: string): BuilderNode => {
    const node = nodesById[nodeId]
    if (!node) {
      // Return a minimal node if not found
      return {
        id: nodeId,
        type: 'root',
        expanded: false,
        children: []
      }
    }

    return {
      ...node,
      children: (node.childIds || []).map(childId => buildTree(childId))
    }
  }

  return rootIds.map(rootId => buildTree(rootId))
}

/**
 * Initialize a new normalized tree state with a single root node
 * @returns Object with nodesById and rootIds
 */
export const initializeTreeState = (): {
  nodesById: NodesById
  rootIds: string[]
} => {
  const rootId = generateRandomId()
  const rootNode = createBuilderNode(rootId, 'root', { state: State.Root })

  return {
    nodesById: {
      [rootId]: {
        ...rootNode,
        parentId: null,
        childIds: []
      } as NormalizedBuilderNode
    },
    rootIds: [rootId]
  }
}
